/** The fixed formula Pricing applies per arc, with the given multiplier. */
export const terMultiplierFormula = (multiplier: string): string =>
  `TER + (1 − TER) × ${multiplier}`;

export const TER_MULTIPLIER_FORMULA = terMultiplierFormula('m');

// A TER has at most 3 decimal places and a multiplier at most 2, so both are exact as integers
// at these scales and every step below stays far inside the safe integer range.
const TER_DECIMALS = 3;
const MULTIPLIER_DECIMALS = 2;

const DECIMAL_PATTERN = /^(\d+)(?:\.(\d+))?$/;

/**
 * `value` times 10^`decimals`, read from its decimal digits rather than through a float. Trailing
 * zeros do not count towards the decimal places, as Pricing strips them.
 */
const scaled = (value: string, decimals: number): number => {
  const match = DECIMAL_PATTERN.exec(value.trim());
  const fraction = match ? (match[2] || '').replace(/0+$/, '') : '';
  if (!match || fraction.length > decimals) {
    throw new Error(`Not a decimal with at most ${decimals} decimal places: ${value}`);
  }
  return Number(match[1]) * 10 ** decimals + Number(fraction.padEnd(decimals, '0'));
};

const hundredthsText = (hundredths: number): string => {
  const fraction = String(hundredths % 100).padStart(2, '0');
  return `${(hundredths - (hundredths % 100)) / 100}.${fraction}`;
};

/**
 * A TER multiplier band's weight for an arc, ROUND_HALF_UP(TER + (1 − TER) × m, 2), with exact
 * decimal arithmetic, as Pricing computes it, so that halves such as 0.505 round as Pricing rounds
 * them.
 */
export const terMultiplierWeight = (ter: string, multiplier: string): string => {
  const terScale = 10 ** TER_DECIMALS;
  const t = scaled(ter, TER_DECIMALS);
  const m = scaled(multiplier, MULTIPLIER_DECIMALS);
  // The weight times 10^(TER_DECIMALS + MULTIPLIER_DECIMALS).
  const exact = t * 10 ** MULTIPLIER_DECIMALS + (terScale - t) * m;
  // Down to hundredths: drop the TER's decimals, rounding half up.
  const shifted = exact + terScale / 2;
  return hundredthsText((shifted - (shifted % terScale)) / terScale);
};

/** A weight with at most 2 decimal places, written with exactly 2, as `terMultiplierWeight` is. */
export const weightText = (weight: string): string =>
  hundredthsText(scaled(weight, MULTIPLIER_DECIMALS));
